import {
  getV1CompaniesCompanyIdJobsJobIdAiSummary,
  getV1CompaniesCompanyIdJobsJobIdLoadsAiSummaries,
  putV1CompaniesCompanyIdJobsJobIdAiSummary,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import connection from '~services/connectionModule';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { AiJobSummary, AiLoadSummary } from './models';

export default function useAiSummaries() {
  const getAiJobSummary = useCallback(
    async (companyId: string, jobId: string, signal?: AbortSignal) => {
      try {
        const response = await getV1CompaniesCompanyIdJobsJobIdAiSummary({
          path: { 'company-id': companyId, 'job-id': jobId },
          signal,
        });

        return AiJobSummary.parse(response.data.data);
      } catch (error) {
        // the endpoint returns 404 until a summary has been generated for the job; the
        // panel renders its not-processed empty state instead of an error toast
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }

        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.ai_summaries.failed_to_fetch_job_summary'),
        );
        throw error;
      }
    },
    [],
  );

  const fetchAllAiLoadSummaries = useCallback(
    async (companyId: string, jobId: string, signal?: AbortSignal) => {
      try {
        return await fetchAllApiPages(
          (query, pageSignal) =>
            getV1CompaniesCompanyIdJobsJobIdLoadsAiSummaries({
              path: { 'company-id': companyId, 'job-id': jobId },
              query,
              signal: pageSignal,
            }),
          AiLoadSummary.parse,
          { signal },
        );
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.ai_summaries.failed_to_fetch_load_summaries'),
        );
        throw error;
      }
    },
    [],
  );

  /**
   * Kicks off an async (re)generation run. The endpoint returns 202 Accepted; the
   * resulting summary arrives on the AiJobSummaryChannel RTU, not in this response.
   * Failures propagate to the caller untouched — the AI summary pane renders them
   * inline instead of routing through the shared toast pipeline.
   */
  const generateAiJobSummary = useCallback(async (companyId: string, jobId: string) => {
    await putV1CompaniesCompanyIdJobsJobIdAiSummary({
      path: { 'company-id': companyId, 'job-id': jobId },
    });
  }, []);

  return { fetchAllAiLoadSummaries, generateAiJobSummary, getAiJobSummary };
}
